import { Component } from "react";

/**
 * Lightweight error boundary for the global search results list.
 *
 * A single thrown render (bad row shape, corrupt server payload, stale
 * data, etc.) must NEVER cascade up to the route and bounce the user
 * back to the project selection screen (iter-121 pilot report).
 * Instead we swallow, log, and render a graceful retry affordance.
 */
export default class SearchErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, message: "" };
    }

    static getDerivedStateFromError(error) {
        return {
            hasError: true,
            message: (error && error.message) || "Something went wrong",
        };
    }

    componentDidCatch(error, info) {
        // eslint-disable-next-line no-console
        console.error("SearchErrorBoundary caught:", error, info);
    }

    reset = () => {
        this.setState({ hasError: false, message: "" });
        this.props.onReset?.();
    };

    render() {
        if (this.state.hasError) {
            return (
                <div
                    data-testid="global-search-error"
                    className="rounded-lg border border-red-500/30 bg-red-500/5 p-4 text-center space-y-3"
                >
                    <p className="text-red-300 font-semibold text-sm">
                        Search hit a snag
                    </p>
                    <p className="text-[11px] text-neutral-400">
                        {this.state.message}
                    </p>
                    <button
                        type="button"
                        data-testid="global-search-error-retry"
                        onClick={this.reset}
                        className="instant-tap inline-flex items-center justify-center h-9 px-4 rounded-md bg-primary text-primary-foreground text-xs font-semibold"
                    >
                        Try again
                    </button>
                </div>
            );
        }
        return this.props.children;
    }
}
